"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { Stethoscope, Calendar, Mountain, Users, Cpu, Network, Sparkles, ArrowRight } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";
import { Badge } from "@/components/ui/badge";

const DIFFERENTIATORS = [
  { icon: Stethoscope, title: "The Medical Method", headline: "Clinical discipline, exported.", body: "The only operator at this level whose discipline was forged in clinical practice. The diagnostic method — evidence, protocol, triage, judgment, outcome accountability, do no harm — is applied to organizations as an actual operating system, not a metaphor.", gradient: "from-red/20 to-red/0", iconBg: "bg-gradient-sovereign", accent: "text-red", border: "border-red/30", metric: "1", metricLabel: "Of its kind" },
  { icon: Calendar, title: "A Decade of Building", headline: "Ten years. One organization.", body: "Not a consulting career. A single organization, co-built and sustained for ten years — from an information-security consultancy into a Digital Conglomerate with 60+ integrated services and operations across 3 continents.", gradient: "from-purple/20 to-purple/0", iconBg: "bg-gradient-royal", accent: "text-purple", border: "border-purple/30", metric: "10+", metricLabel: "Years sustained" },
  { icon: Mountain, title: "Sovereign-Scale Operations", headline: "Stakes measured in national stability.", body: "Experience operating at the level where stakes are national, clients are sovereign, and margin for error is zero. Governance programs, complex campaigns, and strategic operations across 18 countries — through every global disruption.", gradient: "from-emerald/20 to-emerald/0", iconBg: "bg-gradient-success", accent: "text-emerald", border: "border-emerald/30", metric: "18", metricLabel: "Countries" },
  { icon: Cpu, title: "Zero Third-Party Dependencies", headline: "Built in-house. Verifiable. Exclusive.", body: "Operational leadership across an integrated ecosystem of services that cannot be purchased, licensed, or replicated without the same decade of investment. Every capability is in-house. Every advantage is exclusive.", gradient: "from-amber-gold/20 to-amber-gold/0", iconBg: "bg-gradient-premium", accent: "text-amber-gold", border: "border-amber-gold/30", metric: "60+", metricLabel: "In-house services" },
  { icon: Network, title: "Sovereign Operations Architecture", headline: "Six stages. Verified outcomes.", body: "A six-stage methodology refined over hundreds of engagements: Diagnose, Design, Deploy, Operate, Optimize, and Verify. The same architecture that produced verified outcomes for sovereign clients is now codified for repeatable deployment.", gradient: "from-denim/20 to-denim/0", iconBg: "bg-gradient-depth", accent: "text-denim", border: "border-denim/30", metric: "6", metricLabel: "Stage methodology" },
];

export function DifferentiatorsSection() {
  return (
    <Section id="differentiators" ariaLabel="Core Differentiators" background="default">
      <SectionHeader
        icon={Sparkles}
        overline="Differentiators"
        overlineClassName="text-amber-gold"
        pillClassName="glass-tinted-amber-gold border-amber-gold/20"
        title={
          <>
            Six Advantages <span className="text-gradient-premium">No Competitor Can Replicate.</span>
          </>
        }
        subtitle="Each differentiator is grounded in operational reality — not marketing copy."
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
        {DIFFERENTIATORS.map((d, i) => (
          <motion.div
            key={d.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.5, delay: i * 0.06, ease: [0, 0, 0.2, 1] }}
            className={`group relative glass-card rounded-2xl p-6 overflow-hidden bg-gradient-to-br ${d.gradient} border ${d.border}/40`}
          >
            <div aria-hidden className="absolute -top-16 -right-16 size-48 rounded-full bg-current opacity-[0.04] blur-2xl group-hover:scale-150 transition-transform duration-700" />
            <div className="relative">
              <div className="flex items-start justify-between mb-5">
                <div className={`size-12 rounded-xl ${d.iconBg} flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                  <d.icon className="size-6 text-white" />
                </div>
                <div className="text-right">
                  <div className={`text-display-sm leading-none ${d.accent}`}>{d.metric}</div>
                  <div className="text-caption text-muted-foreground mt-1">{d.metricLabel}</div>
                </div>
              </div>
              <h3 className="text-heading-md mb-2">{d.title}</h3>
              <p className="text-caption text-muted-foreground font-semibold mb-3 italic">{d.headline}</p>
              <p className="text-body-sm text-muted-foreground leading-relaxed">{d.body}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 glass-strong rounded-2xl p-6 md:p-8 gradient-border flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="size-14 rounded-2xl bg-gradient-royal flex items-center justify-center shadow-xl">
            <Sparkles className="size-7 text-white" />
          </div>
          <div>
            <div className="text-overline text-purple">Methodology</div>
            <h3 className="text-heading-md">See the 6-stage Sovereign Operations Architecture in action.</h3>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link href="#methodology" className="inline-flex items-center gap-2 text-body-sm font-semibold text-red hover:gap-3 transition-all group">
            How it works <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link href="/expertise/differentiators/" className="inline-flex items-center gap-2 text-body-sm font-semibold text-purple hover:gap-3 transition-all group">
            Full deep-dive <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
        {[
          { cls: "text-red", label: "Medical Method" },
          { cls: "text-purple", label: "Decade" },
          { cls: "text-emerald", label: "Sovereign" },
          { cls: "text-amber-gold", label: "70% Model" },
          { cls: "text-amber-gold", label: "In-house" },
          { cls: "text-denim", label: "Architecture" },
        ].map((s) => (
          <span key={s.label} className="glass rounded-full px-3 py-1.5 text-caption font-medium">
            <span className={s.cls}>·</span> <span className="text-muted-foreground ml-1">{s.label}</span>
          </span>
        ))}
      </div>
    </Section>
  );
}
